/**
 * Live Lecture 1050 — BDU-style calendar (bduedu.in/live-session).
 *
 * Same schema as Live Lecture 1 / 1011 (no new columns); the defaults below are
 * the BDU portal's palette so an unconfigured section renders like the
 * reference. `active_bar_color` is deliberately not exposed — the BDU toolbar
 * marks the active view by filling the button, not with an underline bar.
 *
 * Hardcoded in the widget, matching the reference component: the Submit
 * button's red gradient, the calendar container border, the filter-trigger
 * border, the event hover colour and the filter placeholders.
 */
export const LIVE_LECTURE_1050_CONSTANTS = {
    DEFAULT_ROW: {
        id: "1",
        status: false,
        title: "",
        title_text_color: "#171719",
        title_highlight_color: "#A10113",
        component_background_color: "#FFFFFF",
        day_section_color: "#FFFFFF",
        day_text_color: "#374151",
        view_button_text_color: "#171719",
        view_button_bg_color: "#F3F4F6",
        view_button_active_bg_color: "#B8B8B8",
        view_button_active_text_color: "#171719",
        navigate_button_color: "#171719",
        left_navigate_icon: "",
        right_navigate_icon: "",
        button_icon: "",
        button_text: "Submit",
        button_text_color: "#FFFFFF",
        button_bg_color: "",
        event_color: "#1F2937",
        event_text_color: "#FFFFFF",
        tooltip_color: "#991B1B",
        tooltip_text_color: "#FFFFFF",
        description: "* The above schedule are tentative in nature.",
    },

    LIVE_LECTURE_FIELDS_CONFIG: [
        {
            fieldName: "status",
            label: "Live Lecture Status",
            value: false,
            valueType: "boolean",
            filedType: "TOGGLE",
            placeholder: "Enable or disable live lecture widget",
        },
        {
            fieldName: "title",
            label: "Title",
            value: "",
            valueType: "string",
            filedType: "INPUT",
            placeholder: "Optional heading. Wrap words in {} to highlight them",
        },
        {
            fieldName: "title_text_color",
            label: "Title Text Color",
            value: "#171719",
            valueType: "string",
            filedType: "COLOR",
            placeholder: "Enter title text color (e.g., #171719)",
        },
        {
            fieldName: "title_highlight_color",
            label: "Title Highlight Color",
            value: "#A10113",
            valueType: "string",
            filedType: "COLOR",
            placeholder: "Color for {highlighted} words in the title",
        },
        {
            fieldName: "component_background_color",
            label: "Component Background Color",
            value: "#FFFFFF",
            valueType: "string",
            filedType: "COLOR",
            placeholder: "Enter component background color (e.g., #FFFFFF)",
        },
        {
            fieldName: "day_section_color",
            label: "Day or Week Section Color",
            value: "#FFFFFF",
            valueType: "string",
            filedType: "COLOR",
            placeholder: "Enter day header background color (e.g., #FFFFFF)",
        },
        {
            fieldName: "day_text_color",
            label: "Day or Month Text Color",
            value: "#374151",
            valueType: "string",
            filedType: "COLOR",
            placeholder: "Enter day and month label color (e.g., #374151)",
        },
        {
            fieldName: "view_button_text_color",
            label: "View Button Text Color",
            value: "#171719",
            valueType: "string",
            filedType: "COLOR",
            placeholder: "Enter Month/Day button text color (e.g., #171719)",
        },
        {
            fieldName: "view_button_bg_color",
            label: "View Button Background Color",
            value: "#F3F4F6",
            valueType: "string",
            filedType: "COLOR",
            placeholder: "Enter inactive Month/Day button color (e.g., #F3F4F6)",
        },
        {
            fieldName: "view_button_active_bg_color",
            label: "View Button Active Background Color",
            value: "#B8B8B8",
            valueType: "string",
            filedType: "COLOR",
            placeholder: "Enter active Month/Day button color (e.g., #B8B8B8)",
        },
        {
            fieldName: "view_button_active_text_color",
            label: "View Button Active Text Color",
            value: "#171719",
            valueType: "string",
            filedType: "COLOR",
            placeholder: "Enter active Month/Day text color (e.g., #171719)",
        },
        {
            fieldName: "navigate_button_color",
            label: "Navigate Button Color",
            value: "#171719",
            valueType: "string",
            filedType: "COLOR",
            placeholder: "Enter left/right navigate arrow color (e.g., #171719)",
        },
        {
            fieldName: "left_navigate_icon",
            label: "Left Navigate Icon",
            value: "",
            valueType: "string",
            filedType: "FILE",
            placeholder: "Upload left navigation icon (defaults to a < arrow)",
        },
        {
            fieldName: "right_navigate_icon",
            label: "Right Navigate Icon",
            value: "",
            valueType: "string",
            filedType: "FILE",
            placeholder: "Upload right navigation icon (defaults to a > arrow)",
        },
        {
            fieldName: "button_icon",
            label: "Button Icon",
            value: "",
            valueType: "string",
            filedType: "FILE",
            placeholder: "Upload submit button icon (optional)",
        },
        {
            fieldName: "button_text",
            label: "Button Text",
            value: "Submit",
            valueType: "string",
            filedType: "INPUT",
            placeholder: "Enter filter button text (e.g., Submit)",
        },
        {
            fieldName: "button_text_color",
            label: "Button Text Color",
            value: "#FFFFFF",
            valueType: "string",
            filedType: "COLOR",
            placeholder: "Enter button text color (e.g., #FFFFFF)",
        },
        {
            fieldName: "button_bg_color",
            label: "Button Background Color",
            value: "",
            valueType: "string",
            filedType: "COLOR",
            placeholder:
                "Leave empty for the BDU red gradient (#FF0013 → #990013)",
            defaultValue: "",
        },
        {
            fieldName: "event_color",
            label: "Event Color",
            value: "#1F2937",
            valueType: "string",
            filedType: "COLOR",
            placeholder: "Enter event background color (e.g., #1F2937)",
        },
        {
            fieldName: "event_text_color",
            label: "Event Text Color",
            value: "#FFFFFF",
            valueType: "string",
            filedType: "COLOR",
            placeholder: "Enter event text color (e.g., #FFFFFF)",
        },
        {
            fieldName: "tooltip_color",
            label: "Tooltip Background Color",
            value: "#991B1B",
            valueType: "string",
            filedType: "COLOR",
            placeholder: "Enter tooltip background color (e.g., #991B1B)",
        },
        {
            fieldName: "tooltip_text_color",
            label: "Tooltip Text Color",
            value: "#FFFFFF",
            valueType: "string",
            filedType: "COLOR",
            placeholder: "Enter tooltip text color (e.g., #FFFFFF)",
        },
        {
            fieldName: "description",
            label: "Footer Note",
            value: "* The above schedule are tentative in nature.",
            valueType: "string",
            filedType: "RICH_TEXT_EDITOR",
            placeholder: "Note shown below the calendar",
        },
    ],
};
